/**
 * useHostAccess Hook
 *
 * Answers one question: may the current user reach the brand/host surfaces
 * (campaign registration, applicants, influencer search)?
 *
 * This exists so the route guard and the CTAs that lead to it cannot disagree.
 * Previously `HostOnlyRoute` hand-rolled the check while the main page CTA only
 * looked at `isAuthenticated`, so an influencer could be sent to a page that
 * then rejected them.
 *
 * RBAC permission is checked first; `userType` is the fallback for accounts
 * minted before roles were assigned.
 */

import { useAuthStore } from '../store/authStore';
import { usePermissions } from './usePermissions';
import { isHostLike } from '../constants/userTypes';

/** Permission that gates campaign creation. */
export const CAMPAIGN_CREATE_PERMISSION = 'campaigns:create';

export interface UseHostAccessResult {
  /** True when the user would pass `HostOnlyRoute`. */
  canAccessHostArea: boolean;
  /** Permissions still resolving — callers should not treat `false` as denial. */
  loading: boolean;
}

export function useHostAccess(): UseHostAccessResult {
  const { user } = useAuthStore();
  const { hasPermission, loading } = usePermissions();

  const canAccessHostArea =
    hasPermission(CAMPAIGN_CREATE_PERMISSION) || isHostLike(user?.userType || '');

  return { canAccessHostArea, loading };
}

export default useHostAccess;
